/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import isNil from 'lodash.isnil';
import {clearQueryParams} from '@ion/applications/src/query/state';
import {getAccountById} from '@ion/core/src/entities/Accounts';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {searchAssets} from '@ion/core/src/services/Connect';
import {entityId, mapDeviceSearchResults, setRel} from '@ion/core/src/services/ConnectUtils';
import {getOperatorIdType} from '@ion/core/src/services/GroupProperties';
import {
  Asset,
  CTCEntityResponseWrapper,
  Device,
  Entity,
  GenericObject,
  IOProfile,
  Link,
  Operator,
  Operators,
  Schedules
} from '@ion/core/src/types/entities';
import {parseDateToUnix} from '@ion/core/src/utils/DateUtil';
import {removeDuplicate} from '@ion/core/src/utils/ObjectUtil';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {BadgeType} from '@ion/dls/src/types/editors';
import {setQuery} from '@/routes/RouteDefinitions';
import {fetchDevicesByIdentifiers} from '../deviceSearch';
import {isPreferenceAvailable} from '../helpers';
import {toggleUserPrompt} from './user-prompt';

/**
 * @getGeoFenceFilterSelections
 * Returns only the undeleted filters.
 */
export const getGeoFenceFilterSelections = (geoItems, value) => {
  const selections = [];
  // Checking if values i.e, applied saved filter has any deleted geofences
  geoItems?.forEach(({id}) => {
    const selected = value.filter(selected => selected.id === id);
    if (selected) {
      selections.push(...selected);
    }
  });
  return selections;
};

/**
 * @updateUserPreference
 * Updates the user preference by removing any deleted
 * geofences that might be present in applied saved filter.
 */
export const updateUserPreference = (currentFilters) => {
  const savedFilters = UserPreferences.get('tracking.filters');
  const newFilter = [
    currentFilters,
    ...(savedFilters || [])
  ];
  const newFilterWithoutDuplicate = removeDuplicate('magicKey', newFilter);
  UserPreferences.set(
    'tracking.filters',
    newFilterWithoutDuplicate,
    true
  );
};

export const validateAccounts = <EntityType>(
  accountLinks: Link[],
  entities: Array<CTCEntityResponseWrapper<EntityType>>,
  type: Entity
) => {
  const resolved: Array<CTCEntityResponseWrapper<EntityType>>   = [];
  const unresolved: Array<CTCEntityResponseWrapper<EntityType>> = [];
  entities.forEach(entity => {
    const item: any = type ? entity[type] : entity;
    const {
      groupName,
      parentGroup
    } = item;
    const title           = groupName || parentGroup;
    const matchedAcc      = accountLinks?.filter((accountLink) => accountLink.title === title);
    const matchedAccCount = matchedAcc?.length;
    const isValidAccount  = type === Entity.asset ? matchedAccCount === 1 : !!matchedAccCount;
    if (isValidAccount) {
      const {href, title, status} = matchedAcc[0];
      const rel = groupName ? 'account' : 'parent';
      const tempAccount = {
        rel : 'account',
        href,
        title,
        status
      };
      item[rel] = tempAccount;
      delete item.groupName;
      delete item.parentGroup;
      resolved.push(entity);
    } else {
      if (matchedAccCount > 1) {
        item.hasDuplicateAccount = true;
      }
      unresolved.push(entity);
    }
  });
  return {resolved, unresolved};
};

/**
 * The createDeviceSearchTerms utility iterates the list of imported Assets,
 * and returns an array of searchTerms search terms for each group which
 * contains device ESNS.
 *
 * @method  createDeviceSearchTerms
 * @param   {array} resolved Array of resolved assets which are to be imported.
 *          Assets are considered to be resolved if the group to which they are
 *          to be added exists within the current global group hierarchy.
 * @returns {array} An array of device searchTerm objects, whereby each object
 *          defines all ESN values as the esn search term for each group.
 */
export const createDeviceSearchTerms = (resolved: Array<CTCEntityResponseWrapper<Asset>>): GenericObject[] => {
  const identifiers = {};
  resolved.forEach(({asset}) => {
    const deviceIdentifiers = (asset as GenericObject)?.deviceIdentifiers || {};
    Object.keys(deviceIdentifiers).forEach(identifier => {
      const values = deviceIdentifiers[identifier];
      if (values?.length) {
        identifiers[identifier] = (identifiers[identifier] || []).concat(values);
      }
    });
  });
  return Object.keys(identifiers).map(identifier => ({
    identifierType : identifier,
    identifiers    : identifiers[identifier]
  }));
};

/**
 * Retrieves the Devices matching the identifiers defined against the imported
 * Assets via the Device Smart Search API, which resolves considerably faster
 * than the Device search it replaces. Every identifier type is requested
 * concurrently, and the matched Devices are keyed by account so as to match
 * the shape which the import's validation utilities consume.
 *
 * ```typescript
 *
 *  import {searchDevicesByIdentifiers} from './helpers';
 *
 *  const devices = await searchDevicesByIdentifiers(searchTerms, accountId);
 *
 * ```
 *
 * @param searchTerms The identifier searches as returned by
 *        createDeviceSearchTerms.
 * @param accountId The account within whose hierarchy the Devices are sought.
 * @returns The matched Devices, keyed by the id of the account which holds
 *          each of them.
 */
export const searchDevicesByIdentifiers = async(
  searchTerms: GenericObject[],
  accountId: number
): Promise<Device> => {
  const matched = await Promise.all(searchTerms.map(({identifierType, identifiers}) => (
    fetchDevicesByIdentifiers({accountId, identifierType, identifiers})
  )));
  return mapDeviceSearchResults(matched.flat()) as Device;
};

// defines each Device identifier which the Asset import template exposes as a
// column of its own; a Device is matched against whichever of these it
// carries, rather than against its category ...
const DEVICE_IDENTIFIERS = ['esn', 'serialNumber', 'imei'];

/**
 * Derives the Devices collection url from the account to which an imported
 * Asset resolved, allowing a matched Device to be paired by its own id ...
 */
const getDevicesBaseURL = (account: Link): string => {
  const href = account?.href || '';
  return `${href.substring(0, href.indexOf('/accounts'))}/devices`;
};

/**
 * Resolves every identifier defined against an imported Asset to the Device
 * which the bulk Device search returned for it, pairing the Asset with that
 * Device by its id. A Device listed under more than one identifier column —
 * such as a Camera whose IMEI is also carried as its esn — is paired only
 * once. Assets referencing an identifier which matched no Device are returned
 * as invalid so that the identifier can be reported to the user.
 *
 * @method  resolveAssetDevices
 * @param   {array} assets Array of resolved assets which are to be imported.
 * @param   {object} devices A mapping of group ids which contains the device
 *          search results for each identifier the assets defined.
 * @returns {object} An object providing a valid property which contains the
 *          assets whose every identifier resolved to a Device, and an invalid
 *          property containing those which defined an unknown identifier.
 */
export const resolveAssetDevices = (
  assets: Array<CTCEntityResponseWrapper<Asset>>,
  devices: Device
) => {
  const valid   = [];
  const invalid = [];
  const matches = {};
  Object.keys(devices || {}).forEach(groupId => {
    devices[groupId]?.forEach(device => {
      DEVICE_IDENTIFIERS.forEach(identifier => {
        const value = device?.[identifier];
        if (value) {
          matches[`${identifier}:${value}`] = device;
        }
      });
    });
  });
  assets.forEach(({asset}) => {
    const deviceIdentifiers = (asset as GenericObject)?.deviceIdentifiers || {};
    const paired  = [];
    const missing = [];
    Object.keys(deviceIdentifiers).forEach(identifier => {
      deviceIdentifiers[identifier]?.forEach(value => {
        const device = matches[`${identifier}:${value}`];
        if (!device) {
          missing.push(value);
        } else if (!paired.some(({id}) => id === device.id)) {
          paired.push(device);
        }
      });
    });
    delete (asset as GenericObject).deviceIdentifiers;
    if (missing.length) {
      invalid.push({
        asset : Object.assign(asset, {
          isDeviceNotFound : true,
          missingDevices   : missing.join(', ')
        })
      });
    } else {
      asset.devices = paired.map(device => ({
        href : `${getDevicesBaseURL(asset.account)}/${device.id}`,
        rel  : 'device'
      }));
      valid.push({asset});
    }
  });
  return {valid, invalid};
};

export const createDriverSearchTerms = (resolved: Array<CTCEntityResponseWrapper<Asset>>): GenericObject => {
  if (!resolved?.length) {
    return null;
  }

  const {firstName, lastName} = resolved.reduce((acc, {asset}) => {
    const {operators} = asset;
    if (operators) {
      const [first, last] = operators.split(' ');
      acc.firstName.push(first);
      acc.lastName.push(last);
    }
    return acc;
  }, {firstName: [], lastName: []});

  if (firstName.length && lastName.length) {
    return {
      firstName : firstName.join(','),
      lastName  : lastName.join(',')
    };
  }
  return null;
};

/**
 * The createIOProfileSearchTerms utility iterates the list of imported Assets,
 * and returns an array of searchTerms search terms for each group which
 * contains device ESNS.
 *
 * @method  createIOProfileSearchTerms
 * @param   {array} resolved Array of resolved assets which are to be imported.
 *          Assets are considered to be resolved if the group to which they are
 *          to be added exists within the current global group hierarchy.
 * @returns {array} An array of device searchTerm objects, whereby each object
 *          defines all ESN values as the esn search term for each group.
 */
export const createIOProfileSearchTerms = (resolved: Array<CTCEntityResponseWrapper<any>>): GenericObject[] => {
  const groupIOProfileNames = {};
  const ioProfileNames = [];
  resolved.forEach(({asset}) => {
    const {account, ioProfile} = asset;
    if (ioProfile) {
      const groupId = entityId(account);
      groupIOProfileNames[groupId] = groupIOProfileNames[groupId] || [];
      groupIOProfileNames[groupId].push(ioProfile);
    }
  });
  Object.keys(groupIOProfileNames).forEach(groupId => {
    ioProfileNames.push({
      profileName : {
        value    : groupIOProfileNames[groupId].toString(),
        wildcard : false
      },
      account : {
        value : groupId
      }
    });
  });
  return ioProfileNames;
};

/**
 * The createScheduleSearchTerms utility iterates the list of imported Assets,
 * and returns an array of searchTerms search terms for each group which
 * contains schedules.
 *
 * @method  createScheduleSearchTerms
 * @param   {array} resolved Array of resolved assets which are to be imported.
 *          Assets are considered to be resolved if the group to which they are
 *          to be added exists within the current global group hierarchy.
 * @returns {array} An array of schedule searchTerm objects, whereby each object
 *          defines all schedules as the schedule search term for each group.
 */
export const createScheduleSearchTerms = (
  resolved: Array<CTCEntityResponseWrapper<any>>,
  entity: string): GenericObject[] => {
  const groupScheduleNames = {};
  const scheduleNames = [];
  resolved.forEach(({[entity]: {schedule, account}}) => {
    if (schedule) {
      const groupId = entityId(account);
      groupScheduleNames[groupId] = groupScheduleNames[groupId] || [];
      groupScheduleNames[groupId].push(schedule);
    }
  });
  Object.keys(groupScheduleNames).forEach(groupId => {
    scheduleNames.push({
      name : {
        value    : groupScheduleNames[groupId].toString(),
        wildcard : false
      },
      account : {
        value : groupId
      }
    });
  });
  return scheduleNames;
};

/**
 * Provides a convenience function which validates the device ESNs for the
 * list of imported Assets against the Devices retrieved, returning an object
 * which contains the valid and invalid Assets.
 *
 * @method  validateDeviceGroups
 * @param   {array} assets Array of assets containing devices which are to be
 *          validated.
 * @param   {object} devices A mapping of group ids which contains the device
 *          search results for each asset which defined a device ESN.
 * @returns {object} An object providing a valid property which contains the
 *          list of valid assets with devices, and an invalid property which
 *          contains the list of invalid assets with devices (i.e. either the
 *          device could not be found, or the device is not defined within
 *          the current group hierarchy).
 */
export const validateDeviceGroups = (assets: Array<CTCEntityResponseWrapper<Asset>>, devices: Device) => {
  const valid     = [];
  const invalid   = [];

  // the Asset which presently holds each Device, by device id. A Device which
  // is held by no Asset is absent from this map ...
  const holders = {};
  Object.keys(devices || {}).forEach(groupId => {
    devices[groupId]?.forEach(({id, asset}) => {
      if (!isNil(asset)) {
        holders[id] = asset?.title;
      }
    });
  });

  // the Devices each imported Asset lays claim to, by Asset name. An Asset may
  // claim several Devices, such as an LMU together with a Camera ...
  const claims = {};
  assets.forEach(({asset}) => {
    claims[asset?.name] = (asset?.devices || []).map(device => entityId(device));
  });

  /**
   * A Device may be claimed by the Asset which already holds it, and by an
   * Asset which holds no Device at all. Another Asset may claim it only when
   * the Asset presently holding it is itself part of the import and has
   * released it, which is how a Device is moved between two Assets within a
   * single import. Where the holder retains it, or is absent from the import
   * altogether, the claim is reported against the claiming Asset ...
   */
  const isUnavailableTo = (deviceId: number, assetName: string): boolean => {
    const holder = holders[deviceId];
    let unavailable = false;
    if (!isNil(holder) && holder !== assetName) {
      const holderClaims = claims[holder];
      unavailable = isNil(holderClaims) || holderClaims.includes(deviceId);
    }
    return unavailable;
  };

  assets.forEach(({asset}) => {
    const claimed = claims[asset?.name] || [];
    const hasUnavailableDevice = claimed.some(deviceId => isUnavailableTo(deviceId, asset?.name));
    if (hasUnavailableDevice) {
      invalid.push({asset});
    } else {
      valid.push({asset});
    }
  });
  return {valid, invalid};
};

/**
 * Retrieves the Devices presently associated with each existing Asset which the
 * import references by id. Every Asset is requested within a single search, as
 * an Asset id is unique regardless of the account in which the Asset resides.
 * Imported rows which carry no id define new Assets and are not requested.
 *
 * ```typescript
 *
 *  import {getCurrentAssetDevices} from './helpers';
 *
 *  const currentDevices = await getCurrentAssetDevices(resolved);
 *  // {6840: [14807], 446573: [459605, 459606]}
 *
 * ```
 *
 * @param assets The resolved assets which are to be imported.
 * @returns The ids of the Devices associated with each existing Asset, keyed
 *          by the Asset id. An Asset absent from the result does not exist.
 */
export const getCurrentAssetDevices = async(
  assets: Array<CTCEntityResponseWrapper<Asset>>
): Promise<GenericObject> => {
  const ids = Array.from(new Set(assets
    .map(({asset}) => asset?.id)
    .filter(id => !isNil(id) && `${id}` !== '')));
  const current = {};
  if (ids.length) {
    const existing: Asset[] = await searchAssets({id: ids.join(',')}, ids.length);
    existing?.forEach(asset => {
      current[asset.id] = (asset?.devices || []).map(device => entityId(device));
    });
  }
  return current;
};

/**
 * Validates the imported Assets for a user who is not permitted to link or
 * de-link Devices from Assets. Such a user may import an Asset only when its
 * Devices are left as they are, which is the case when the Device identifier
 * columns are left blank, or when they list exactly the Devices the Asset
 * presently holds. As the Asset is saved with the Devices listed, listing only
 * some of them de-links the remainder, so any difference is rejected. A new
 * Asset holds no Device, so it may be created only without one.
 *
 * ```typescript
 *
 *  import {validateDeviceAssociation} from './helpers';
 *
 *  const {valid, invalid} = validateDeviceAssociation(resolved, currentDevices);
 *
 * ```
 *
 * @param assets The resolved assets which are to be imported, each paired with
 *        the Devices its identifier columns resolved to.
 * @param currentDevices The Devices associated with each existing Asset, as
 *        returned by getCurrentAssetDevices.
 * @returns An object providing a valid property which contains the assets
 *          which may be imported, and an invalid property containing those
 *          which would change the Devices of an Asset.
 */
export const validateDeviceAssociation = (
  assets: Array<CTCEntityResponseWrapper<Asset>>,
  currentDevices: GenericObject
) => {
  const valid   = [];
  const invalid = [];
  assets.forEach(({asset}) => {
    const current  = currentDevices?.[asset?.id];
    const imported = (asset?.devices || []).map(device => entityId(device));
    const isUnchanged = !imported.length || (
      imported.length === current?.length &&
      imported.every(deviceId => current.includes(deviceId))
    );
    if (!isUnchanged) {
      invalid.push({
        asset : Object.assign(asset, {isDeviceAssociationRestricted: true})
      });
    } else {
      valid.push({asset});
    }
  });
  return {valid, invalid};
};

// defines the sentinel value entered in the Driver import column (or selected
// via the Driver dropdown in the generated template) to explicitly un-pair a
// driver from an asset; comparison is case-insensitive ...
export const NONE_DRIVER_VALUE = 'None';

export const validateDrivers = (
  assets: Array<CTCEntityResponseWrapper<any>>,
  drivers: Operators,
  isAutomaticReAssignmentDriver?: boolean
) => {
  const valid   = [];
  const invalid = [];

  const driversMap = drivers.reduce((acc, driver) => {
    const name = ((driver as any).fullName || `${driver.firstName} ${driver.lastName}`).trim();
    if (acc[name]) {
      acc[name].push(driver);
    } else {
      acc[name] = [driver];
    }
    return acc;
  }, {});

  for (const {asset} of assets) {
    const {operators, id: assetId} = asset;
    const normalizedOperators = operators?.toString().trim();
    if (!normalizedOperators) {
      // A blank Driver cell leaves the currently assigned driver untouched;
      // propertyResolver ignores nil values so the existing driver is kept ...
      asset.operators = null;
      valid.push({asset});
      continue;
    }
    if (normalizedOperators.toLowerCase() === NONE_DRIVER_VALUE.toLowerCase()) {
      // Only the explicit "None" value un-pairs the currently assigned driver.
      // forceOperatorsClear tells propertyResolver that this nil value is
      // intentional and must overwrite the existing driver ...
      asset.operators = null;
      asset.forceOperatorsClear = true;
      valid.push({asset});
      continue;
    }
    const matchingDrivers = driversMap[normalizedOperators];
    const count = matchingDrivers?.length;

    if (!count) {
      asset.isDriverNotFound = true;
      invalid.push({asset});
      continue;
    }
    if (count > 1) {
      asset.isMultipleDrivers = true;
      invalid.push({asset});
      continue;
    }
    const matchingDriver = matchingDrivers[0];
    const driverAssetId = entityId(matchingDriver.asset);
    if (!isAutomaticReAssignmentDriver && driverAssetId && (assetId === null || assetId !== driverAssetId)) {
      asset.isAssetDriverAlreadyExisting = true;
      asset.alreadyExistingAsset = matchingDriver.asset?.title || '';
      invalid.push({asset});
      continue;
    }
    asset.operators = [{
      ...matchingDriver._self,
      rel : 'operator'
    }];

    valid.push({asset});
  }

  return {valid, invalid};
};

export const validateIOProfileGroups = (assets: Array<CTCEntityResponseWrapper<any>>, ioprofiles: IOProfile) => {
  const valid     = [];
  const invalid   = [];
  const validated = {};
  const hrefs = {};
  Object.keys(ioprofiles).forEach(groupId => {
    const results = ioprofiles[groupId];
    results.forEach((ioprofile) => {
      validated[ioprofile.profileName] = true;
      hrefs[ioprofile.profileName] = ioprofile._self.href;
    });
  });
  assets.forEach(({asset}) => {
    const {ioProfile} = asset;
    if (ioProfile && !validated[ioProfile]) {
      invalid.push({asset});
    } else {
      if (ioProfile && hrefs[ioProfile]) {
        asset.ioProfile = {
          href : hrefs[ioProfile],
          rel  : 'ioProfile'
        };
      }
      valid.push({asset});
    }
  });
  return {valid, invalid};
};

export const validateSchedules = (
  resolved: Array<CTCEntityResponseWrapper<any>>,
  schedules: Schedules,
  entity: string) => {
  const valid     = [];
  const invalid   = [];
  const validated = {};
  const hrefs = {};
  Object.keys(schedules)?.forEach(groupId => {
    const results = schedules[groupId];
    validated[groupId] = {};
    hrefs[groupId] = {};
    results?.forEach(schedule => {
      validated[groupId][schedule.name] = true;
      hrefs[groupId][schedule.name] = schedule._self?.href;
    });
  });
  resolved?.forEach(({[entity]: item}) => {
    const {schedule, account} = item;
    if (schedule) {
      const groupId = entityId(account);
      if (!validated[groupId]?.[schedule]) {
        item.isScheduleNotFound = true;
        invalid.push({[entity]: item});
      } else {
        item.schedules = [{
          href : hrefs[groupId][schedule],
          rel  : 'schedule'
        }];
        valid.push({[entity]: item});
      }
    } else {
      item.schedules = null;
      valid.push({[entity]: item});
    }
  });
  return {valid, invalid};
};

/**
 * The getDevicesToMove utility iterates the list of Devices,
 * and returns an array of Devices that should move to child accounts.
 *
 * Only the Assets which passed validateDeviceGroups reach this utility, so
 * every Device claimed here is one the Asset is entitled to. That includes a
 * Device released by another Asset within the same import, which must still be
 * moved when the two Assets sit in different accounts ...
 *
 * @method  getDevicesToMove
 * @param   {array} resolved Array of resolved assets which are to be imported.
 *  @param  {array} devices Array of devices objects which are to be move.
 * @returns {array} An array of device objects that they should move to child account
 */
export const getDevicesToMove = (resolved: Array<CTCEntityResponseWrapper<Asset>>, devices: Device) => {
  const deviceAccountIds = {};
  const updated = [];
  if (!isNil(devices)) {
    Object.keys(devices).forEach(key => {
      devices[key].forEach(({id, account}) => {
        deviceAccountIds[id] = entityId(account);
      });
    });

    resolved.forEach(({asset}) => {
      const {account, devices: paired} = asset;
      paired?.forEach(deviceToMove => {
        const deviceId = entityId(deviceToMove);
        const accountId = deviceAccountIds[deviceId];
        if (accountId !== entityId(account) && devices[accountId]) {
          devices[accountId].forEach(device => {
            if (device.id === deviceId) {
              device.account = account;
              if (updated.findIndex(update => update.id === device.id) === -1) {
                updated.push(device);
              }
            }
          });
        }
      });
    });
  }
  return updated;
};

/**
 * The getAssetsWithDeviceMoves utility returns the Assets whose paired Device
 * is one of the Devices getDevicesToMove identified as needing to move. The
 * CTC Platform rejects an Asset's own account change while a Device is still
 * associated to it, so these Assets must have that Device disassociated
 * before the Device - and then the Asset - can move to the new account.
 *
 * @method  getAssetsWithDeviceMoves
 * @param   {array} resolved      Array of resolved assets which are to be imported.
 * @param   {array} devicesToMove Array of device objects returned by getDevicesToMove.
 * @returns {array} An array of Assets whose paired Device is moving accounts
 */
export const getAssetsWithDeviceMoves = (
  resolved: Array<CTCEntityResponseWrapper<Asset>>, devicesToMove: Device[]
): Asset[] => {
  const movingDeviceIds = devicesToMove.map(device => device.id);
  return resolved
    .map(({asset}) => asset)
    .filter(asset => asset.devices?.some(deviceLink => movingDeviceIds.includes(entityId(deviceLink))));
};

/**
 * The getOperatorsToMove utility mirrors getDevicesToMove, but for the Driver
 * (Operator) paired to each Asset - it iterates the list of Drivers and returns
 * an array of Drivers that should move to the Asset's destination account.
 *
 * @method  getOperatorsToMove
 * @param   {array} resolved Array of resolved assets which are to be imported.
 * @param   {array} drivers Array of Driver entities which are to be moved.
 * @returns {array} An array of Driver entities that should move to the Asset's account
 */
export const getOperatorsToMove = (resolved: Array<CTCEntityResponseWrapper<Asset>>, drivers: Operators) => {
  const operatorAccountIds = {};
  const updated = [];
  if (!isNil(drivers)) {
    drivers.forEach(driver => {
      operatorAccountIds[driver.id] = entityId(driver.account);
    });

    resolved.forEach(({asset}) => {
      const {account, operators: paired} = asset;
      const operatorId = paired?.length ? entityId(paired[0]) : null;
      if (operatorId && operatorAccountIds[operatorId] !== entityId(account)) {
        const driver = drivers.find(({id}) => id === operatorId);
        if (driver && updated.findIndex(update => update.id === driver.id) === -1) {
          driver.account = setRel(account, 'account');
          updated.push(driver);
        }
      }
    });
  }
  return updated;
};

/**
 * Given a list of entities, iterates the list and determines if the group name
 * specified is contained within the current global group hierarchy, returning
 * an object containing a 'valid' and 'invalid' property.
 *
 * Entities whose group can be resolved within the current global group hierarchy
 * will be returned in the 'resolved' list, and entities whose group can not be
 * resolved will be returned in the 'unresolved' list.
 *
 * Entities that can be resolved which contain a 'groupName' property will have
 * an 'account' attributed added with the given account's _self attribute assigned
 * as the account value, and a 'rel' attribute set to 'account'.
 *
 * Entities that can be resolved which contain a 'parentGroup' property will have
 * a 'parent' attributed added with the given account's _self attribute assigned
 * as the parent value, and a 'rel' attribute set to 'parent'.
 *
 * In the context of entity imports, the 'groupName' or 'parentGroup' properties
 * are to be defined as column values in the localized "import" entry. For example:
 *
 *   "Group Name": "groupName"
 *   "Parent Group Name": "parentGroup"
 *
 * ```typescript
 *   import {resolveLinks} from '@/store/actions/helpers';
 *
 *   export const importAssetsConfirm = (assets, mode) => {
 *     return (dispatch, getStore) => {
 *       const hierarchy = getStore().globalGroupChooser.get('currentSubGroupNames');
 *       const {resolved, unresolved} = resolveLinks(assets, hierarchy);
 *       console.log(resolved); // list of entities augmented with account or parent links
 *       console.log(invalid); // list of entities which do not reside in the current hierarchy
 *       ...
 *     }
 *   };
 *```
 *
 * @param     entities An array of entities which are to have their account
 *           attribute resolved.
 * @param    subGroups globalGroupChooser currentSubGroupNames prop.
 * @param    type An optional CTC entity type (such as 'asset'). This argument
 *           can be used for resolving nested objects where each entity is wrapped in
 *           an enclosing object and the entity is defined within a top-level attribute;
 *           for example: {asset:{...}}. In this example, to resolve the entity, the
 *           type parameter can be passed as 'asset', which will result in the entity
 *           being resolved against entity.asset.
 * @returns  An object containing a 'resolved' property, which contains each
 *           entity whose group exists within current global group hierarchy, and an
 *           'unresolved' property which contains each entity whose group does not
 *           exist within current global.
 *
 */
export const resolveLinks = <EntityType>(
  entities: Array<CTCEntityResponseWrapper<EntityType>>,
  subGroups: Link[],
  type: Entity) => {
  const resolved: Array<CTCEntityResponseWrapper<EntityType>>   = [];
  const unresolved: Array<CTCEntityResponseWrapper<EntityType>>   = [];
  entities.forEach(entity => {
    const item  = type ? entity[type] : entity;
    const title = (item as any).groupName || (item as any).parentGroup;
    const link = subGroups?.filter((accountLink) => accountLink.title === title)[0];
    if (link?.href) {
      const rel   = (item as any).groupName ? 'account' : 'parent';
      item[rel] = {href: link.href, title, rel};
      delete (item as any).groupName;
      delete (item as any).parentGroup;
      resolved.push(entity);
    } else {
      unresolved.push(entity);
    }
  });
  return {resolved, unresolved};
};

/**
 * The method helps that sets the current grid column state in redux state and if
 * the user has auto grid save preference in extended attribute this
 * will set the column state in user preference. This can also resuable when the force update
 * done by the user using the grid save option which is on top of grid.
 *
 * ```typescript
 *
 *  import {dispatchGridColumnsChange}  from '@/store/actions/helper';
 *
 *  const dispatch = useDispatch();
 *  dispatch(dispatchGridColumnsChange(columns, 'adminAssets', 'SET_GRID_COLUMNS'));
 *
 * ```
 *
 * @param columnState The Current column state from grid.
 * @param reducer     A name of the reducer. ex. adminAssets.
 * @param type        The type of an action.
 */
export const dispatchGridColumnsChange = (
  columnState: ColumnState[],
  reducer: string,
  type: string
) => {
  return (dispatch, getState) => {
    if (columnState) {
      const {[reducer]: {grid}} = getState();
      const autoSaveGrid        = isPreferenceAvailable('persistGridLayouts');
      dispatch({
        type,
        columnState : {
          autoSaveGrid,
          preferedColumnState : grid?.preferedColumnState,
          nextColumnState     : columnState
        }
      });
    }
  };
};

/**
 * This method maps crash Events to HVS Events. Retains crash properties but
 * adds the ones needed for displaying HVS events (ex. on a grid or a map).
 *
 * ```typescript
 *
 * import {mapCrashToHVSEvent} from './helpers'
 *
 * const events = mapCrashToHVSEvent(crashEvents);
 *
 * ```
 *
 * @param crashEvents An array of CrashBoxx event records.
 */
export const mapCrashToHVSEvent = (crashEvents) => {
  return crashEvents.map((crashEvent) => {
    crashEvent.eventCode     = 999; // [ION-SUITE TODO]: Will this be coming in the response? Added manually for now
    crashEvent.eventTime     = parseDateToUnix(crashEvent.crashDate);
    crashEvent.deviceEsn     = crashEvent.deviceId;
    crashEvent.postalCode    = crashEvent.zip;
    crashEvent.state         = crashEvent.stateProvinceCd;
    crashEvent.street        = crashEvent.street1;
    crashEvent.OBJECTID      = crashEvent.id;
    crashEvent.operatorNames = '';
    crashEvent.id            = crashEvent.assetId;
    return crashEvent;
  });
};

/**
 * Defines a utility to filter selected items that are in the current account hierarchy
 *
 * ```typescript
 *
 * import {getItemAllowed} from './helpers;
 * const itemsAllowed = getItemAllowed(selected, globalGroupHierarchy);
 *
 *  ```
 *
 * @param selected array of selected items
 * @param accountHierarchy account hierarchy
 * @returns array of allowed items
 */
export const getItemAllowed = (selected: any, accountHierarchy: any, identifier = 'accountId') => {
  const itemAllowed = [];
  if (selected?.length && accountHierarchy) {
    selected.forEach(item => {
      if (item?.[identifier]) {
        const account = getAccountById(accountHierarchy, parseInt(item[identifier]));
        account && itemAllowed.push(item);
      }
    });
  }
  return itemAllowed;
};

/**
 * Validates a badge number by checking its length and format.
 *
 * ```typescript
 *
 * import {validateBadge} from './helpers;
 *
 * const isValid = validateBadge(badgeNumber);
 *
 *  ```
 *
 * @param badgeNumber - The badge number to validate.
 * @returns The validated badge number (trimmed if necessary) or null if invalid.
 */
export const validateBadge = (badgeNumber: string) => {
  if (badgeNumber.length === 16) {
    // Trim the first 2 and last 2 characters
    badgeNumber = badgeNumber.substring(2, 14);
  } else if (badgeNumber.length !== 12) {
    // If length is neither 16 nor 12, it's invalid
    return null;
  }
  return badgeNumber;
};

/**
 * Validates badge ID values for a list of operators, especially when the badge type is iButton.
 *
 * ```typescript
 *
 * import {validateKeyfobBadgeIDValues} from './helpers;
 *
 * const {valid, invalid} = validateKeyfobBadgeIDValues(operators);
 *
 *  ```
 *
 * @param operators - An array of operator objects to validate.
 * @returns An object containing arrays of valid and invalid operator objects.
 */
export const validateKeyfobBadgeIDValues = (operators: CTCEntityResponseWrapper<Operator>[]) => {
  const type = getOperatorIdType();
  if (type === BadgeType.iButton) {
    const valid = [];
    const invalid = [];
    operators.forEach(({operator}) => {
      const {badgeNumber = ''} = operator;
      if (!isEmpty(badgeNumber)) {
        const id = validateBadge(badgeNumber.toString());
        if (id) {
          valid.push({
            operator : {
              ...operator,
              badgeNumber : id
            }
          });
        } else {
          invalid.push({
            operator : {
              ...operator,
              invalidBadge : true
            }
          });
        }
      } else {
        valid.push({operator});
      }
    });
    return {valid, invalid};
  }
  return {valid: operators, invalid: []};
};

/**
 * The action dispatches an access error related to page access and features.
 *
 * @param forceToDisplay The flag force the toggle to display error message
 * @param message  The error message to display in the toggle.
 */
export const toggleAccessError = (forceToDisplay = false, message = 'nav:deepLinkRestriction') => {
  return async(dispatch: Function, getState: Function) => {
    const postInitializationComplete = getState().initialization?.postInitializationComplete;
    if (postInitializationComplete || forceToDisplay) {
      setQuery();
      dispatch(clearQueryParams());
      dispatch(toggleUserPrompt({
        display             : true,
        autoHide            : true,
        autoHideDuration    : 10000,
        actionLabel         : 'common:dismiss',
        maxWidth            : '50vw',
        displayOnlyInLayout : true,
        message
      }));
    }
  };
};
